/* global Excel */

export async function getSelectedXmlCells() {
  return Excel.run(async (context) => {
    const range = context.workbook.getSelectedRange();
    range.load(["values", "rowCount", "columnCount", "rowIndex", "columnIndex", "address", "worksheet/name"]);
    await context.sync();

    const cells = [];
    for (let row = 0; row < range.rowCount; row++) {
      for (let col = 0; col < range.columnCount; col++) {
        const value = range.values[row][col];
        if (value === null || value === undefined || String(value).trim() === "") {
          continue;
        }
        cells.push({
          rowIndex: range.rowIndex + row,
          columnIndex: range.columnIndex + col,
          value: String(value),
        });
      }
    }

    return {
      worksheetName: range.worksheet.name,
      sourceAddress: range.address,
      cells,
    };
  });
}

export async function writeParsedColumns({ worksheetName, sourceColumnIndex, sourceRowIndices, headers, values }) {
  return Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(worksheetName);
    const outputColumnStart = sourceColumnIndex + 1;
    const firstDataRow = Math.min(...sourceRowIndices);

    // Keep parsed values on the same rows as the source XML.
    // Headers go on the row above. If XML starts on row 0, insert a sheet
    // row so headers have a place and source + output stay aligned.
    let headerRow = firstDataRow > 0 ? firstDataRow - 1 : 0;
    let dataRowShift = 0;

    if (headers.length > 0 && firstDataRow === 0) {
      sheet.getRangeByIndexes(0, 0, 1, 1).getEntireRow().insert(Excel.InsertShiftDirection.down);
      headerRow = 0;
      dataRowShift = 1;
      await context.sync();
    }

    if (headers.length > 0) {
      const headerRange = sheet.getRangeByIndexes(headerRow, outputColumnStart, 1, headers.length);
      headerRange.values = [headers];
      headerRange.format.font.bold = true;
    }

    sourceRowIndices.forEach((rowIndex, rowOffset) => {
      const rowValues = values[rowOffset];
      if (!rowValues || rowValues.length === 0) return;

      const outputRowIndex = rowIndex + dataRowShift;
      const rowRange = sheet.getRangeByIndexes(outputRowIndex, outputColumnStart, 1, rowValues.length);
      rowRange.values = [rowValues];
    });

    if (headers.length > 0) {
      const rowCount = sourceRowIndices.length + 1;
      const outputRange = sheet.getRangeByIndexes(headerRow, outputColumnStart, rowCount, headers.length);
      outputRange.format.autofitColumns();
    }

    await context.sync();
  });
}

/**
 * Write a full table (headers + rows) starting at the active selection.
 * Used for folder / file imports where there is no source XML column.
 * Writes in chunks so large imports (1000+ rows) do not drop the last rows.
 */
export async function writeTableAtSelection({ headers, values }) {
  return Excel.run(async (context) => {
    const range = context.workbook.getSelectedRange();
    range.load(["rowIndex", "columnIndex", "worksheet/name"]);
    await context.sync();

    if (!headers.length) {
      return { worksheetName: range.worksheet.name, address: range.address };
    }

    const startRow = range.rowIndex;
    const startCol = range.columnIndex;
    const sheet = context.workbook.worksheets.getItem(range.worksheet.name);
    const rowCount = values.length + 1;
    const chunkSize = 200;

    const headerRange = sheet.getRangeByIndexes(startRow, startCol, 1, headers.length);
    headerRange.values = [headers];
    headerRange.format.font.bold = true;
    await context.sync();

    for (let offset = 0; offset < values.length; offset += chunkSize) {
      const chunk = values.slice(offset, offset + chunkSize);
      const dataRange = sheet.getRangeByIndexes(startRow + 1 + offset, startCol, chunk.length, headers.length);
      dataRange.values = chunk;
      await context.sync();
    }

    const outputRange = sheet.getRangeByIndexes(startRow, startCol, Math.max(rowCount, 1), headers.length);
    outputRange.format.autofitColumns();
    outputRange.load("address");
    await context.sync();

    return {
      worksheetName: range.worksheet.name,
      address: outputRange.address,
    };
  });
}
